// src/components/Experiment/ClassificationManager.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Form, Button, Badge, Card, Row, Col, Modal, Alert,
  InputGroup, Dropdown, ListGroup
} from 'react-bootstrap';
import './ClassificationManager.css';

const ClassificationManager = ({
  samples,
  classes,
  onUpdateSample,
  onUpdateClasses,
  selectedClassGroup,
  onSelectClassGroup
}) => {
  const [showNewClassModal, setShowNewClassModal] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [showSampleSearch, setShowSampleSearch] = useState({});
  const [sampleSearchTerm, setSampleSearchTerm] = useState({});
  const [tempClassSamples, setTempClassSamples] = useState({});
  const [showHelp, setShowHelp] = useState(false); // New state for help toggle

  const searchInputRefs = useRef({});

  // Get all class types
  const classTypes = Object.keys(classes || {});

  // NEW FUNCTION: Update both samples and classes together
  const updateSchema = (updatedClasses, updatedSamples = null) => {
    // Create a new schema object with updated classes
    const newSchema = {
      classes: updatedClasses,
      samples: updatedSamples || samples // Use provided samples or keep existing
    };

    // Send both updates together
    onUpdateSample('SCHEMA_UPDATE', newSchema);
  };

  // Create new classification group
  const handleAddClassGroup = () => {
    const newGroupName = `group${classTypes.length + 1}`;
    const updatedClasses = {
      ...classes,
      [newGroupName]: []
    };

    updateSchema(updatedClasses);
    onSelectClassGroup(newGroupName);
  };


  // Start editing group name
  const handleStartEditGroup = (groupName) => {
    setEditingGroupId(groupName);
    setEditingGroupName(groupName);
  };

  // Save group name edit
  const handleSaveGroupEdit = () => {
    if (!editingGroupName.trim() || editingGroupName === editingGroupId) {
      setEditingGroupId(null);
      return;
    }

    // Check if new name already exists
    if (classTypes.includes(editingGroupName) && editingGroupName !== editingGroupId) {
      alert(`Group name "${editingGroupName}" already exists. Please choose a different name.`);
      return;
    }

    const updatedClasses = { ...classes };
    updatedClasses[editingGroupName] = updatedClasses[editingGroupId];
    delete updatedClasses[editingGroupId];

    // Update ALL samples that reference the old group name
    const updatedSamples = samples.map(sample => {
      if (sample.classes && sample.classes[editingGroupId] !== undefined) {
        const newClasses = { ...sample.classes };
        newClasses[editingGroupName] = newClasses[editingGroupId];
        delete newClasses[editingGroupId];
        return {
          ...sample,
          classes: newClasses
        };
      }
      return sample;
    });

    // Update both classes and samples together
    updateSchema(updatedClasses, updatedSamples);

    if (selectedClassGroup === editingGroupId) {
      onSelectClassGroup(editingGroupName);
    }
    setEditingGroupId(null);
  };

  // Cancel group edit
  const handleCancelGroupEdit = () => {
    setEditingGroupId(null);
    setEditingGroupName('');
  };

  // Delete classification group
  const handleDeleteGroup = (groupName) => {
    if (!window.confirm(`Delete classification group "${groupName}" and all its classes?`)) {
      return;
    }

    const updatedClasses = { ...classes };
    delete updatedClasses[groupName];

    // Remove group from all samples
    const updatedSamples = samples.map(sample => {
      if (sample.classes && sample.classes[groupName] !== undefined) {
        const newClasses = { ...sample.classes };
        delete newClasses[groupName];
        return {
          ...sample,
          classes: newClasses
        };
      }
      return sample;
    });

    // Update both classes and samples together
    updateSchema(updatedClasses, updatedSamples);

    // Select first available group or none
    const remainingGroups = Object.keys(updatedClasses);
    if (selectedClassGroup === groupName) {
      onSelectClassGroup(remainingGroups.length > 0 ? remainingGroups[0] : '');
    }
  };

  // Add new class to selected group
  const handleAddClass = () => {
    if (!selectedClassGroup) return;

    const existingClasses = classes[selectedClassGroup] || [];
    const newClassName = `class${existingClasses.length + 1}`;

    setNewClassName(newClassName);
    setShowNewClassModal(true);
  };

  const handleConfirmNewClass = () => {
    if (!newClassName.trim() || !selectedClassGroup) return;

    const updatedClasses = {
      ...classes,
      [selectedClassGroup]: [
        ...(classes[selectedClassGroup] || []),
        {
          id: Date.now(),
          name: newClassName.trim(),
          color: `hsl(${Math.random() * 360}, 70%, 60%)`
        }
      ]
    };

    updateSchema(updatedClasses);
    setNewClassName('');
    setShowNewClassModal(false);
  };

  // Delete class
  const handleDeleteClass = (classId) => {
    if (!selectedClassGroup) return;

    const classToDelete = classes[selectedClassGroup].find(cls => cls.id === classId);
    if (!window.confirm(`Delete class "${classToDelete?.name}"?`)) {
      return;
    }

    const updatedClasses = {
      ...classes,
      [selectedClassGroup]: classes[selectedClassGroup].filter(cls => cls.id !== classId)
    };

    // Remove this class from all samples
    const updatedSamples = samples.map(sample => {
      if (sample.classes && sample.classes[selectedClassGroup] === classId) {
        const newClasses = { ...sample.classes };
        delete newClasses[selectedClassGroup];
        return {
          ...sample,
          classes: newClasses
        };
      }
      return sample;
    });

    // Update both classes and samples together
    updateSchema(updatedClasses, updatedSamples);
  };

  // Get samples for a class
  const getClassSamples = (classId) => {
    return samples.filter(sample =>
      sample.classes && sample.classes[selectedClassGroup] === classId
    );
  };

  // Get unassigned samples for current group
  const getUnassignedSamples = () => {
    return samples.filter(sample =>
      !sample.classes || !sample.classes[selectedClassGroup]
    );
  };

  // Get assigned samples for current group
  const getAssignedSamples = () => {
    return samples.filter(sample =>
      sample.classes && sample.classes[selectedClassGroup]
    );
  };

  // Toggle sample search dropdown
  const toggleSampleSearch = (classId) => {
    setShowSampleSearch(prev => ({
      ...prev,
      [classId]: !prev[classId]
    }));

    if (!showSampleSearch[classId]) {
      setSampleSearchTerm(prev => ({ ...prev, [classId]: '' }));
      // Focus input after dropdown opens
      setTimeout(() => {
        if (searchInputRefs.current[classId]) {
          searchInputRefs.current[classId].focus();
        }
      }, 100);
    }
  };

  // Add sample to class
  const handleAddSampleToClass = (classId, sampleId) => {
    const sample = samples.find(s => s.id === sampleId);
    if (!sample) return;

    const updatedSamples = samples.map(s => {
      if (s.id === sampleId) {
        return {
          ...s,
          classes: {
            ...s.classes,
            [selectedClassGroup]: classId
          }
        };
      }
      return s;
    });

    // Update samples only (classes remain the same)
    updateSchema(classes, updatedSamples);

    // Close search dropdown
    setShowSampleSearch(prev => ({ ...prev, [classId]: false }));
    setSampleSearchTerm(prev => ({ ...prev, [classId]: '' }));
  };

  // Remove sample from class
  const handleRemoveSampleFromClass = (sampleId) => {
    const sample = samples.find(s => s.id === sampleId);
    if (!sample) return;

    const updatedSamples = samples.map(s => {
      if (s.id === sampleId) {
        const newClasses = { ...s.classes };
        delete newClasses[selectedClassGroup];
        return {
          ...s,
          classes: newClasses
        };
      }
      return s;
    });

    // Update samples only (classes remain the same)
    updateSchema(classes, updatedSamples);
  };

  // Filter samples based on search term
  const filterSamples = (samplesList, searchTerm) => {
    if (!searchTerm) return samplesList;
    return samplesList.filter(sample =>
      sample.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  if (samples.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <i className="bi bi-grid-3x3 fs-1 mb-2 d-block"></i>
        <p>Define samples first to assign classes</p>
      </div>
    );
  }

  return (
    <div className="classification-manager">
      {/* Help Toggle Button and Tips Section - Only shown when toggled */}
      <Card className="mb-3" style={{ display: showHelp ? 'block' : 'none' }}>
        <Card.Header className="d-flex justify-content-between align-items-center py-2 bg-info bg-opacity-10">
          <div className="d-flex align-items-center">
            <i className="bi bi-lightbulb-fill text-info fs-5 me-2"></i>
            <h6 className="mb-0">Classification Help</h6>
          </div>
          <Button
            size="sm"
            variant="outline-info"
            onClick={() => setShowHelp(false)}
            title="Close help"
          >
            <i className="bi bi-x-lg"></i>
          </Button>
        </Card.Header>
        <Card.Body className="py-2">
          <div className="row">
            <div className="col-md-6">
              <h6 className="small fw-bold mb-2">Getting Started:</h6>
              <ul className="small text-muted mb-3">
                <li>Create classification groups for different attributes (e.g., Time, Freshness)</li>
                <li>Add classes within each group (e.g., "Fresh", "After 5 min")</li>
                <li>Assign samples to classes using the methods below</li>
              </ul>
            </div>
            <div className="col-md-6">
              <h6 className="small fw-bold mb-2">Assignment Methods:</h6>
              <div className="small text-muted">
                <div className="mb-2">
                  <i className="bi bi-keyboard-fill text-warning me-1"></i>
                  <strong>Multi-select Method:</strong> Hold CTRL + Right-click circles
                </div>
                <div className="mb-2">
                  <i className="bi bi-mouse-fill text-primary me-1"></i>
                  <strong>Quick Method:</strong> Right-click on any circle
                </div>
                <div>
                  <i className="bi bi-search text-primary me-1"></i>
                  <strong>Search Method:</strong> Use search boxes in each class below
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 pt-2 border-top">
            <h6 className="small fw-bold mb-2">Advanced Multi-Select:</h6>
            <div className="small text-muted">
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex mb-2">
                    <i className="bi bi-ctrl text-info me-2"></i>
                    <div>
                      <strong>Hold CTRL</strong> + <strong>Right-click</strong> on circles
                    </div>
                  </div>
                  <div className="d-flex mb-2">
                    <i className="bi bi-check-square text-success me-2"></i>
                    <div>Selected circles turn <span className="text-success fw-bold">green</span></div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex mb-2">
                    <i className="bi bi-mouse text-info me-2"></i>
                    <div>Right-click on any selected circle to assign all</div>
                  </div>
                  <div className="d-flex">
                    <i className="bi bi-layers text-success me-2"></i>
                    <div>Assign the same class to multiple samples at once</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 pt-2 border-top">
            <h6 className="small fw-bold mb-2">Quick Tips:</h6>
            <div className="small text-muted">
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex mb-1">
                    <i className="bi bi-palette text-success me-2"></i>
                    <span>Sample circles show class colors when assigned</span>
                  </div>
                  <div className="d-flex mb-1">
                    <i className="bi bi-three-dots-vertical text-success me-2"></i>
                    <span>Click ⋮ on groups to rename or delete them</span>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex mb-1">
                    <i className="bi bi-x-circle text-danger me-2"></i>
                    <span>Click <i className="bi bi-x"></i> on sample tags to remove assignments</span>
                  </div>
                  <div className="d-flex">
                    <i className="bi bi-eye text-success me-2"></i>
                    <span>Select a group to see assigned colors on the plate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 pt-2 border-top bg-light bg-opacity-50 p-2 rounded">
            <h6 className="small fw-bold mb-2 text-primary">
              <i className="bi bi-lightning-charge-fill me-1"></i>
              Pro Tip:
            </h6>
            <div className="small text-muted">
              Use <kbd className="bg-info text-white">CTRL</kbd> + <strong>Right-click</strong> to select multiple samples, then
              <strong> right-click</strong> on any selected circle to assign them all to the same class in one click!
              <div className="mt-1">
                <small className="text-info">
                  <i className="bi bi-info-circle me-1"></i>
                  Look for the "CTRL: Multi-select mode" indicator in the bottom right when holding CTRL
                </small>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Classification Groups Block */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center py-2">
          <div>
            <h6 className="mb-0">Classification Groups</h6>
            <small className="text-muted">Create groups like "Time", "Freshness", etc.</small>
          </div>
          <div className="d-flex gap-1">
            <Button
              size="sm"
              variant="outline-info"
              onClick={() => setShowHelp(!showHelp)}
              title={showHelp ? "Hide help" : "Show help"}
              active={showHelp}
            >
              <i className="bi bi-question-lg"></i>
            </Button>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={handleAddClassGroup}
              title="Add new classification group"
            >
              <i className="bi bi-plus"></i>
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="py-2">
          {classTypes.length === 0 ? (
            <div className="text-center text-muted py-2">
              <small>No classification groups yet. Click + to create one.</small>
            </div>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {classTypes.map(groupName => (
                <div key={groupName} className="classification-group-item">
                  {editingGroupId === groupName ? (
                    <InputGroup size="sm" style={{ width: '200px' }}>
                      <Form.Control
                        type="text"
                        value={editingGroupName}
                        onChange={(e) => setEditingGroupName(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') handleSaveGroupEdit();
                          if (e.key === 'Escape') handleCancelGroupEdit();
                        }}
                        onBlur={handleSaveGroupEdit}
                        autoFocus
                      />
                      <Button
                        variant="outline-success"
                        onClick={handleSaveGroupEdit}
                      >
                        <i className="bi bi-check"></i>
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={handleCancelGroupEdit}
                      >
                        <i className="bi bi-x"></i>
                      </Button>
                    </InputGroup>
                  ) : (
                    <div className="d-flex align-items-center">
                      <Badge
                        bg={selectedClassGroup === groupName ? 'primary' : 'secondary'}
                        className="classification-group-badge"
                        style={{ cursor: 'pointer' }}
                        onClick={() => onSelectClassGroup(groupName)}
                      >
                        {groupName} ({classes[groupName]?.length || 0})
                      </Badge>
                      <Dropdown className="ms-1">
                        <Dropdown.Toggle
                          variant="link"
                          size="sm"
                          className="p-0 text-muted"
                          style={{ fontSize: '0.8rem' }}
                        >
                          <i className="bi bi-three-dots-vertical"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => handleStartEditGroup(groupName)}>
                            <i className="bi bi-pencil me-2"></i>Edit Name
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item
                            className="text-danger"
                            onClick={() => handleDeleteGroup(groupName)}
                          >
                            <i className="bi bi-trash me-2"></i>Delete Group
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Classes Assignment Block */}
      {selectedClassGroup && (
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center py-2">
            <div>
              <h6 className="mb-0">
                Classes Assignment
                <small className="text-muted ms-2">({selectedClassGroup})</small>
              </h6>
              <small className="text-muted">Create classes within the selected group</small>
            </div>
            <Button
              size="sm"
              variant="outline-success"
              onClick={handleAddClass}
              title="Add new class"
            >
              <i className="bi bi-plus"></i>
            </Button>
          </Card.Header>
          <Card.Body className="py-2">
            {!classes[selectedClassGroup] || classes[selectedClassGroup].length === 0 ? (
              <div className="text-center text-muted py-3">
                <i className="bi bi-tags fs-4 d-block mb-2"></i>
                <small>No classes in this group yet. Click + to create your first class.</small>
                {showHelp && (
                  <div className="mt-2">
                    <small className="text-muted">
                      <i className="bi bi-mouse me-1"></i>
                      Tip: Right-click on plate circles to assign samples after creating classes
                    </small>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Assignment methods reminder - Only shown when help is active */}
                {showHelp && (
                  <div className="mb-3 p-2 bg-info bg-opacity-10 rounded border border-info border-opacity-25">
                    <small className="text-info d-flex align-items-center">
                      <i className="bi bi-mouse-fill me-2"></i>
                      <div>
                        <strong>Two ways to assign samples:</strong>
                        <br />
                        1. <strong>Visual method:</strong> Right-click on circles in the plate
                        <br />
                        2. <strong>Search method:</strong> Use search boxes below for each class
                      </div>
                    </small>
                  </div>
                )}

                <div className="classes-list">
                  {classes[selectedClassGroup].map(classItem => {
                    const classSamples = getClassSamples(classItem.id);
                    const searchTerm = sampleSearchTerm[classItem.id] || '';
                    const unassignedSamples = filterSamples(getUnassignedSamples(), searchTerm);
                    const assignedSamples = filterSamples(getAssignedSamples(), searchTerm);

                    return (
                      <Card key={classItem.id} className="mb-2 class-item">
                        <Card.Body className="py-2">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="d-flex align-items-center">
                              <div
                                className="class-color-indicator me-2"
                                style={{ backgroundColor: classItem.color }}
                              ></div>
                              <strong>{classItem.name}</strong>
                              <Badge bg="light" text="dark" className="ms-2">
                                {classSamples.length} sample{classSamples.length !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteClass(classItem.id)}
                              title="Delete class"
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>

                          {/* Sample Assignment */}
                          <div className="sample-assignment">
                            <Dropdown
                              show={showSampleSearch[classItem.id]}
                              onToggle={() => toggleSampleSearch(classItem.id)}
                            >
                              <Dropdown.Toggle
                                as="div"
                                className="sample-search-input"
                              >
                                <InputGroup size="sm">
                                  <Form.Control
                                    ref={el => searchInputRefs.current[classItem.id] = el}
                                    type="text"
                                    placeholder="Search and add samples..."
                                    value={searchTerm}
                                    onChange={(e) => setSampleSearchTerm(prev => ({
                                      ...prev,
                                      [classItem.id]: e.target.value
                                    }))}
                                    onFocus={() => setShowSampleSearch(prev => ({
                                      ...prev,
                                      [classItem.id]: true
                                    }))}
                                  />
                                  <Button
                                    variant="outline-secondary"
                                    onClick={() => toggleSampleSearch(classItem.id)}
                                  >
                                    <i className={`bi bi-chevron-${showSampleSearch[classItem.id] ? 'up' : 'down'}`}></i>
                                  </Button>
                                </InputGroup>
                              </Dropdown.Toggle>

                              <Dropdown.Menu className="sample-dropdown-menu">
                                {unassignedSamples.length > 0 && (
                                  <>
                                    <Dropdown.Header>Unassigned Samples</Dropdown.Header>
                                    {unassignedSamples.map(sample => (
                                      <Dropdown.Item
                                        key={sample.id}
                                        onClick={() => handleAddSampleToClass(classItem.id, sample.id)}
                                      >
                                        <i className="bi bi-circle me-2 text-muted"></i>
                                        {sample.name}
                                      </Dropdown.Item>
                                    ))}
                                  </>
                                )}

                                {assignedSamples.length > 0 && unassignedSamples.length > 0 && (
                                  <Dropdown.Divider />
                                )}

                                {assignedSamples.length > 0 && (
                                  <>
                                    <Dropdown.Header>Already Assigned</Dropdown.Header>
                                    {assignedSamples.map(sample => {
                                      const assignedClass = classes[selectedClassGroup].find(
                                        cls => cls.id === sample.classes[selectedClassGroup]
                                      );
                                      return (
                                        <Dropdown.Item
                                          key={sample.id}
                                          disabled
                                          className="text-muted"
                                        >
                                          <i className="bi bi-check-circle me-2"></i>
                                          {sample.name}
                                          <small className="ms-1">({assignedClass?.name})</small>
                                        </Dropdown.Item>
                                      );
                                    })}
                                  </>
                                )}

                                {unassignedSamples.length === 0 && assignedSamples.length === 0 && (
                                  <Dropdown.Item disabled>
                                    No samples found
                                  </Dropdown.Item>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>

                            {/* Assignment status - Only show tips when help is active */}
                            {classSamples.length === 0 && showHelp && (
                              <div className="mt-1">
                                <small className="text-muted">
                                  <i className="bi bi-info-circle me-1"></i>
                                  No samples assigned yet. Search above or right-click on plate circles.
                                </small>
                              </div>
                            )}

                            {/* Assigned Samples Tags */}
                            {classSamples.length > 0 && (
                              <div className="assigned-samples mt-2">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <small className="text-muted">Assigned samples:</small>
                                  {showHelp && (
                                    <small className="text-muted">
                                      Click <i className="bi bi-x"></i> to remove
                                    </small>
                                  )}
                                </div>
                                {classSamples.map(sample => (
                                  <Badge
                                    key={sample.id}
                                    bg="light"
                                    text="dark"
                                    className="me-1 mb-1 sample-tag"
                                  >
                                    {sample.name}
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0 ms-1 text-danger"
                                      onClick={() => handleRemoveSampleFromClass(sample.id)}
                                      title="Remove from class"
                                    >
                                      <i className="bi bi-x"></i>
                                    </Button>
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {/* New Class Modal */}
      <Modal show={showNewClassModal} onHide={() => setShowNewClassModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Class</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Class Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter class name..."
                value={newClassName}
                onChange={(e) => setNewClassName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleConfirmNewClass()}
                autoFocus
              />
              <Form.Text className="text-muted">
                Examples: "Fresh", "After 5 min", "High quality"
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewClassModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmNewClass}
            disabled={!newClassName.trim()}
          >
            Add Class
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ClassificationManager;